import { SeriesBaseStrategy } from './base-series.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { singleLineName } from '../../../core/utils/single-line-name.js';
import { DEFAULT_COLOR_PALETTE_RECT } from '../../constants/defaults.js';
import type { XYChartSelectionRectangleMetadata } from '../../hooks/useSortRectangleDatapoints.js';
import {
  RANGE_LEGEND_TYPE,
  type DatapointMetadata,
  type RectDatapointInternal,
  type RectMetadata,
  type RectSeriesIdToValueScaleDomain,
  type RectSeriesWithDatapoints,
  type XYChartAxisInternal,
  type XYChartRectSeriesInternal,
  type XYChartVariantsInternal,
} from '../../types/xy-chart-internals.js';
import type { XYChartColorPalette, XYChartData } from '../../types/xy-chart.js';
import {
  isRectSeries,
  isStringOrValidNumber,
} from '../../utils/type-guards.js';
import type {
  MutableProcessingContext,
  RectSeriesConstructor,
} from '../types/processing-types.js';
import { getRectDatapoint } from '../utils/access-datapoint/get-datapoints.js';
import { getLegendType } from '../utils/cast-data/get-legend-type.js';
import { getValidColorPalette } from '../utils/coloring/get-valid-color-palette.js';
import { getValueScaleDomain } from '../utils/scale/get-value-scale-domain.js';

export class RectSeriesStrategy extends SeriesBaseStrategy<XYChartSelectionRectangleMetadata> {
  public seriesIdToValueScaleDomain: RectSeriesIdToValueScaleDomain;

  constructor(initializer: RectSeriesConstructor) {
    super(initializer);
    this.seriesIdToValueScaleDomain = new Map();
  }

  canHandle(
    seriesProps: XYChartVariantsInternal,
  ): seriesProps is XYChartRectSeriesInternal {
    return isRectSeries(seriesProps);
  }

  protected getSeriesId(
    _datum: XYChartData,
    seriesProps: XYChartRectSeriesInternal,
  ): string {
    return seriesProps.id;
  }

  protected createSeries(
    seriesId: string,
    seriesProps: XYChartRectSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ) {
    return {
      id: seriesId,
      type: seriesProps.type,
      data: [],
      filteredData: [],
      xAxisId: xAxis.id,
      yAxisId: yAxis.id,
      labelAccessor: seriesProps._labelAccessor,
    };
  }

  protected createSeriesMetadata(
    _datum: XYChartData,
    seriesProps: XYChartRectSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    seriesId: string,
    context: MutableProcessingContext,
  ) {
    const { intl, seriesIndexes } = context;

    const color = this.getColor(seriesProps.colorPalette, context.colorPalette);
    const seriesIndex = this.getOrCreateSeriesIndex(seriesId, seriesIndexes);
    const seriesName = this.getSeriesName(intl, seriesIndex, undefined);
    const name = singleLineName(seriesName);

    return {
      name,
      nameSegments: Array.isArray(seriesName) ? seriesName : undefined,
      xAxis,
      yAxis,
      actions: seriesProps.actions,
      valueFormatter: seriesProps.valueFormatter,
      valueUnit: seriesProps.valueUnit,
      colorPalette: color.colorPalette,
      valueAccessor: seriesProps.valueAccessor,
      colorIsFromSlot: color.colorIsFromSlot,
      valueType: undefined,
      valueAccessorLabel: seriesProps.valueAccessorLabel,
      valueMin: seriesProps.valueMin,
      valueMax: seriesProps.valueMax,
    };
  }

  protected processImpl(
    datum: XYChartData,
    seriesProps: XYChartRectSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
    datapoint: RectDatapointInternal,
    context: MutableProcessingContext,
  ) {
    const { seriesMap, seriesToMetadata } = context;

    const seriesId = this.getSeriesId(datum, seriesProps);

    let currentSeries: RectSeriesWithDatapoints | undefined =
      seriesMap.get(seriesId);

    if (!currentSeries) {
      currentSeries = this.createSeries(seriesId, seriesProps, xAxis, yAxis);

      this.registerSeries(currentSeries, seriesId, context);

      const metadata = this.createSeriesMetadata(
        datum,
        seriesProps,
        xAxis,
        yAxis,
        seriesId,
        context,
      );
      // When filtering is active, isVisible starts as false and is set to true
      // once any visible datapoint (category) is encountered.
      // When no filtering, isVisible stays undefined (all visible).
      seriesToMetadata.set(currentSeries, {
        ...metadata,
        isVisible: context.filteredSeriesIds !== undefined ? false : undefined,
      });
    }

    const datapointMetadata: DatapointMetadata = { isGap: true };

    // Value domain logic (unique to RectSeriesStrategy)
    if (isStringOrValidNumber(datapoint.value)) {
      const valueDomain = getValueScaleDomain(
        datapoint.value,
        seriesProps.id,
        this.seriesIdToValueScaleDomain,
      );

      this.seriesIdToValueScaleDomain.set(seriesProps.id, valueDomain);

      datapointMetadata.isGap = false;

      const tempSeriesMetadata = safeGet<
        RectSeriesWithDatapoints,
        RectMetadata
      >(seriesToMetadata, currentSeries);

      tempSeriesMetadata.valueType ??= getLegendType(datapoint.value);

      // Range Legend (number values) associated datapoints are always visible.
      const isDatapointVisible =
        tempSeriesMetadata.valueType === RANGE_LEGEND_TYPE ||
        context.filteredSeriesIds === undefined ||
        context.filteredSeriesIds.has(`${seriesId}--${datapoint.value}`);

      datapointMetadata.isVisible = isDatapointVisible;
      tempSeriesMetadata.isVisible ||= isDatapointVisible;
    }

    if (datapoint.label) {
      currentSeries.hasLabels = true;
    }

    this.registerDatapoint(
      datapoint,
      datapointMetadata,
      currentSeries,
      context,
    );

    const datapointIsVisible =
      datapointMetadata.isVisible === undefined ||
      datapointMetadata.isVisible === true;

    if (datapointIsVisible) {
      context.axisWithSeries.add(xAxis.id);
      context.axisWithSeries.add(yAxis.id);
      if (!datapointMetadata.isGap) {
        this.addValue({
          series: currentSeries,
          datapoint,
        });
      }
    }

    this.updateAxisDomains(
      datapoint,
      xAxis,
      yAxis,
      context.axisIdToScaleDomain,
      datapointIsVisible,
    );
  }

  getColor(
    colorPalette: XYChartColorPalette | undefined,
    fallbackColorPalette: XYChartColorPalette | undefined,
  ) {
    const validColorPalette = getValidColorPalette(
      colorPalette,
      fallbackColorPalette ?? DEFAULT_COLOR_PALETTE_RECT,
    );
    return {
      colorPalette: validColorPalette,
      colorIsFromSlot: validColorPalette === colorPalette,
    };
  }

  getDatapoint(
    datum: XYChartData,
    seriesProps: XYChartRectSeriesInternal,
    xAxis: XYChartAxisInternal,
    yAxis: XYChartAxisInternal,
  ) {
    return getRectDatapoint(datum, seriesProps, xAxis, yAxis);
  }
}
